import { Button } from 'antd';
import { Components } from '@fasstech/flexiget';
import { applySpec, filter, isNil, path, pathEq, pathOr, prop, propEq, propOr } from 'ramda';
import React, { useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { notEqual } from 'ramda-adjunct';
import { EditOutlined, HistoryOutlined } from '@ant-design/icons';
import { Actions, Title } from '../../components';
import CreateVersion from '../../components/CreateVersion';
import ItemComments from '../../components/ItemComments';
import Review from '../../components/Review';
import StatusActions from '../../components/StatusActions';
import withUser from '../../withUser';
import { SCOPE_OFFER } from '../../_CONST';
import QOfferComments from '../../_graphql/queries/comments/QOfferComments';
import useFetchQuery from '../../hooks/useFetchQuery';
import { QFoldersQuery } from '../../_graphql/queries/folders/QFolders';
import Loading from '../../components/Loading';
import UpdateOfferNameMutation from '../../_graphql/mutations/offers/UpdateOfferNameMutation';
import useActionGuard from '../../hooks/useActionGuard';
import { errorMessage, successMessage } from '../../utils/messageMutation';
import FullscreenLoading from '../../components/FullscreenLoading';
import { QOfferQuery } from '../../_graphql/queries/offers/QOffer';
import useOfferRole from '../../hooks/useOfferRole';
import GlobalVariablesSection from './editOffer/GlobalVariablesSection';
import TagsSection from './editOffer/TagsSection';
import WidgetsSection from './editOffer/WidgetList';
import DocumentsList from './editOffer/documentsSection/DocumentsList';

const EditOffer = ({
  userIsAdmin,
  userTeam,
  user
}) => {
  const { offerId } = useParams();
  const history = useHistory();
  const nameOfferRef = useRef();
  const [refetchWidgets, setRefetchWidgets] = useState(false);
  const [refetchTags, setRefetchTags] = useState(false);
  const [focusOfferName, setFocusOfferName] = useState(false);
  const [isLoadingOfferItem, setIsLoadingOfferItem] = useState(false);
  const [fullscreenLoading, setFullscreenLoading] = useState(false);
  const { run } = useActionGuard();

  const { folders, reFetch, isLoading } = useFetchQuery({
    query: QFoldersQuery,
    args: { offerId, scope: SCOPE_OFFER },
    dataProp: 'folders',
    defaultData: [],
    cacheKey: ['offerViewCache', `folders-${offerId}`],
    staleWhileRevalidate: true
  });

  const { offer, reFetch: reFetchOffer, isLoading: isLoadingOffer } = useFetchQuery({
    query: QOfferQuery,
    args: { offerId },
    dataProp: 'offer',
    defaultData: null
  });

  const { userIsOfferEditor } = useOfferRole(offerId);

  if (isLoadingOffer) return <Loading />;

  if (isNil(offer)) {
    return <span>Aucune offre trouvée.</span>;
  }
  if (notEqual(prop('teamId', userTeam), prop('team', offer))) return <span>{'Vous n\'avez pas accès à cette offre.'}</span>;

  const {
    isDraft,
    isReview,
    isPublished,
    versionId
  } = applySpec({
    isDraft: pathEq('draft', ['version', 'status']),
    isReview: pathEq('review', ['version', 'status']),
    isPublished: pathEq('release', ['version', 'status']),
    versionId: path(['version', 'versionId'])
  })(offer);

  const userCanEditOffer = isDraft && (userIsAdmin || userIsOfferEditor);

  const onBlurOfferName = () => {
    const newOfferName = path(['current', 'innerText'], nameOfferRef);
    if (notEqual(propOr('', 'offerName', offer), newOfferName)) {
      const rootOfferId = pathOr(offerId, ['version', 'itemRootId'], offer);
      run(`update-offer-name-${rootOfferId}`, (done) => UpdateOfferNameMutation({ rootOfferId, offerName: newOfferName }, (ok, error) => {
        if (ok && !error) {
          successMessage('offre', 'modifiée', true, true);
          reFetchOffer(true);
        } else {
          errorMessage();
        }
        done(ok, error);
      }));
    }
    setFocusOfferName(false);
  };

  const onKeyDownOfferName = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  return (
    <>
      {fullscreenLoading && <FullscreenLoading />}
      <Actions
        title={(
          <Title
            title="mon offre :"
            infoTitle={() => (
              <>
                <span
                  ref={nameOfferRef}
                  className="text-lg"
                  suppressContentEditableWarning
                  contentEditable={focusOfferName}
                  spellCheck={false}
                  onBlur={onBlurOfferName}
                  onKeyDown={onKeyDownOfferName}
                >
                  {propOr('', 'offerName', offer)}
                </span>
                {userCanEditOffer && (
                  <Components.Tooltip title="Éditer">
                    <EditOutlined
                      onClick={() => {
                        setFocusOfferName(true);
                        nameOfferRef.current.focus();
                      }}
                      className="hover:text-flexi-grey pl-2"
                    />
                  </Components.Tooltip>
                )}
              </>
            )}
            backTo="/"
          />
        )}
        buttons={(
          <>
            <Button
              size="large"
              onClick={() => history.push(`/offre/${offerId}/historique`)}
              icon={<HistoryOutlined />}
            >
              {'Voir l\'historique'}
            </Button>

            <CreateVersion
              versionId={versionId}
              userCanCreateVersion={userIsAdmin}
              redirect="/offre/:itemId/edition"
              version={prop('version', offer)}
              offerId={offerId}
              userId={prop('userId', user)}
            />

            <StatusActions
              item={offer}
              draftLabel="Passer l'offre en édition"
              reviewLabel="Passer l'offre en revue"
              userCanValidate={userIsAdmin}
              userIsOfferEditor={userIsOfferEditor}
              reFetchOffer={reFetchOffer}
            />
          </>
        )}
      />

      <Review
        item={offer}
        userCanValidate={userIsAdmin}
        reFetch={reFetchOffer}
      />
      {isDraft && !userIsAdmin && (
        <QOfferComments args={{ offerId }}>
          {offerComments => {
            return (
              <ItemComments
                comments={offerComments}
                title="Commentaires de revue"
              />
            );
          }}
        </QOfferComments>
      )}

      <DocumentsList
        isOfferPath
        args={{
          scope: SCOPE_OFFER, offerId, type: 'DOCUMENT'
        }}
        offerStatus={path(['version', 'status'], offer)}
        setRefetchWidgets={setRefetchWidgets}
        isPublished={isPublished}
        isReview={isReview}
        cacheKey={['offerViewCache', `documents-${offerId}`]}
        folders={filter(propEq('DOCUMENT', 'type'))(folders)}
        isLoadingFolders={isLoading}
        reFetchFolders={reFetch}
        setIsLoadingOfferItem={setIsLoadingOfferItem}
        isLoadingOfferItem={isLoadingOfferItem}
        setFullscreenLoading={setFullscreenLoading}
        setRefetchTags={setRefetchTags}
      />

      <WidgetsSection
        isOfferPath
        args={{
          offerId, scope: SCOPE_OFFER, type: 'WIDGET'
        }}
        offerIsDraft={isDraft}
        scope={SCOPE_OFFER}
        isPublished={isPublished}
        refetchWidgets={refetchWidgets}
        setRefetchWidgets={setRefetchWidgets}
        cacheKey={['offerViewCache', `widgets-${offerId}`]}
        folders={filter(propEq('WIDGET', 'type'))(folders)}
        isLoadingFolders={isLoading}
        reFetchFolders={reFetch}
        setIsLoadingOfferItem={setIsLoadingOfferItem}
        isLoadingOfferItem={isLoadingOfferItem}
        setFullscreenLoading={setFullscreenLoading}
      />

      <TagsSection
        offerId={offerId}
        userCanEditOffer={userCanEditOffer}
        refetchTags={refetchTags}
        setRefetchTags={setRefetchTags}
      />

      <GlobalVariablesSection
        offerId={offerId}
        userCanEditOffer={userCanEditOffer}
      />
    </>
  );
};

export default withUser(EditOffer);
